/* ============================================================
   Modelos de IA, preço e custo

   Mora no core porque três lados precisam da MESMA tabela: a função que
   cobra os tokens, o painel que mostra o custo, e o painel de novo quando
   projeta "quanto custaria no outro modelo". Três cópias divergiriam, e a
   divergência aqui aparece como número de reunião errado.

   OS TOKENS SÃO FATO — a própria API devolve quantos entraram e saíram.
   O PREÇO É TABELA, e tabela envelhece.

   A LISTA DE MODELOS vem da API do Google ao vivo (`models.list`), mas o
   PREÇO não: aquele endpoint devolve nome, descrição e limite de token, e
   preço nenhum. Então o painel junta as duas coisas — a lista de lá, o
   preço daqui. É por isso que um modelo novo aparece no painel como "sem
   preço" em vez de aparecer barato: ver `PRECOS`.

   ------------------------------------------------------------
   Por que o custo era R$ 0,00 no painel

   Dois erros empilhados, e os dois davam zero em silêncio:

   1. a tabela tinha um modelo e a função chamava outro. Modelo fora da
      tabela caía num `?? { entrada: 0, saida: 0 }` — preço zero, custo
      zero, nenhum aviso.
   2. o acumulado era guardado em CENTAVOS DE DÓLAR como inteiro. Uma
      estimativa custa por volta de US$ 0,0008, ou seja 0,08 centavo:
      `Math.round(0.08)` é 0. Mesmo com a tabela certa, TODA chamada
      arredondava para zero antes de somar.

   O segundo é o mais traiçoeiro, porque continuaria zerando depois de
   consertar o primeiro. Agora o acumulado é em MICRODÓLARES (milionésimos
   de dólar), que é fino o bastante para uma chamada só, e continua inteiro
   — `increment` com fração acumula erro de ponto flutuante, e ninguém quer
   explicar numa reunião por que o custo tem quinze casas decimais.
   ============================================================ */

/** Preço por MILHÃO de tokens, em dólar. */
export interface PrecoDoModelo {
  entrada: number;
  saida: number;
}

/**
 * Preço por modelo, da página de preços do Google (nível pago).
 *
 * CONFIRA em ai.google.dev/gemini-api/docs/pricing antes de levar o
 * número a uma reunião. Esta tabela é a única coisa que separa o custo do
 * painel da fatura de verdade.
 *
 * DUAS SIMPLIFICAÇÕES, as duas seguras para o que o Nhomi faz:
 *
 * - modelos com faixa por tamanho de prompt (Pro cobra mais acima de
 *   200 mil tokens) entram aqui com a faixa DE BAIXO. Uma foto de prato
 *   dá uns 1.500 tokens; a faixa de cima não é alcançável estimando
 *   caloria.
 * - modelos com preço diferente para áudio entram com o preço de
 *   texto/imagem/vídeo. O Nhomi manda imagem.
 *
 * MODELO SEM LINHA AQUI NÃO É ESCOLHÍVEL NO PAINEL. É de propósito:
 * preço desconhecido vira custo errado, e custo errado com cara de certo
 * foi exatamente o bug que este arquivo existe para não deixar acontecer
 * de novo. Modelo novo é uma linha aqui.
 */
export const PRECOS: Record<string, PrecoDoModelo> = {
  "gemini-3.6-flash": { entrada: 1.50, saida: 7.50 },
  "gemini-3.5-flash": { entrada: 1.50, saida: 9.00 },
  "gemini-3.5-flash-lite": { entrada: 0.30, saida: 2.50 },
  "gemini-3.1-flash-lite": { entrada: 0.25, saida: 1.50 },
  "gemini-3.1-pro-preview": { entrada: 2.00, saida: 12.00 },
  "gemini-3-flash-preview": { entrada: 0.50, saida: 3.00 },
  "gemini-2.5-pro": { entrada: 1.25, saida: 10.00 },
  "gemini-2.5-flash": { entrada: 0.30, saida: 2.50 },
  "gemini-2.5-flash-lite": { entrada: 0.10, saida: 0.40 },
};

/**
 * O que roda quando ninguém escolheu nada.
 *
 * É o que já estava rodando antes de a escolha existir, e continua sendo
 * de propósito: trocar o padrão aqui trocaria o modelo em produção num
 * deploy, sem ninguém decidir isso. Existe modelo mais barato na tabela
 * — o painel mostra quais e quanto cada um custaria. A troca é lá.
 */
export const MODELO_PADRAO = "gemini-3.5-flash-lite";

/** As três famílias que o painel mostra. O resto não serve pra isto. */
export type FamiliaDeModelo = "lite" | "flash" | "pro";

/**
 * A que família um id pertence, ou `null` se não for nenhuma das três.
 *
 * A ordem importa: "flash-lite" é LITE, não flash, e testar "flash"
 * primeiro classificaria todo lite como flash. Modelo de imagem e de
 * áudio caem fora por não ter linha em `PRECOS`, não por esta função —
 * ela só olha o nome.
 */
export function familiaDoModelo(id: unknown): FamiliaDeModelo | null {
  const s = String(id ?? "").toLowerCase();
  if (s.includes("lite")) return "lite";
  if (s.includes("flash")) return "flash";
  if (s.includes("pro")) return "pro";
  return null;
}

/** Tem preço cadastrado? É a única definição de "escolhível". */
export const modeloConhecido = (id: unknown): boolean =>
  Object.prototype.hasOwnProperty.call(PRECOS, String(id));

/**
 * O preço de um modelo, com queda no padrão.
 *
 * Nunca devolve zero para modelo desconhecido: zero some do custo sem
 * deixar rastro, e foi assim que o painel passou meses mostrando R$ 0,00
 * com a IA rodando. Um número aproximado é corrigível; um zero é
 * indistinguível de "não usou".
 *
 * Isso é a REDE, não o caminho normal — o caminho normal é o painel só
 * deixar escolher modelo que tem linha em `PRECOS`. A rede existe porque
 * alguém pode editar `config/app` na mão, ou um modelo sair da tabela
 * depois de já estar configurado.
 */
export const precoDoModelo = (id: unknown): PrecoDoModelo =>
  PRECOS[String(id)] ?? PRECOS[MODELO_PADRAO] ?? { entrada: 0.10, saida: 0.40 };

/** Um modelo como o painel desenha: o que a API disse + o preço daqui. */
export interface ModeloDeIA extends PrecoDoModelo {
  id: string;
  /** `displayName` da API, ou o id quando ela não respondeu. */
  nome: string;
  descricao: string;
  familia: FamiliaDeModelo;
  entradaMax: number;
  saidaMax: number;
}

/**
 * A lista que o painel mostra quando a API não respondeu.
 *
 * Sem nome bonito e sem descrição, mas com id e preço — que é o que a
 * escolha precisa. Uma tela vazia porque o Google está fora do ar seria
 * pior: o modelo em uso continua rodando, e quem abriu o painel
 * provavelmente veio justamente trocar ele.
 */
export function modelosDeReserva(): ModeloDeIA[] {
  return Object.entries(PRECOS)
    .map(([id, preco]) => ({
      id,
      nome: id,
      descricao: "",
      familia: familiaDoModelo(id) ?? "flash",
      entradaMax: 0,
      saidaMax: 0,
      ...preco,
    }))
    .sort((a, b) => a.entrada - b.entrada || a.id.localeCompare(b.id));
}

/** O dólar usado para converter. Também é estimativa, também é corrigível. */
export const DOLAR_PADRAO = 5.4;

/**
 * Quanto custam estes tokens, em MICRODÓLARES inteiros.
 *
 * O preço é por MILHÃO de tokens, e micro é um milionésimo — então os
 * dois milhões se cancelam e a conta é só `tokens * preço`. É o motivo de
 * a unidade ser essa: ela cabe uma chamada sozinha sem arredondar para
 * zero, e a conta não tem divisão nenhuma para perder precisão.
 */
export function microsDaChamada(
  preco: PrecoDoModelo, entrada: number, saida: number,
): number {
  const e = Number.isFinite(entrada) ? Math.max(0, entrada) : 0;
  const s = Number.isFinite(saida) ? Math.max(0, saida) : 0;
  return Math.round(e * preco.entrada + s * preco.saida);
}

/** Microdólares -> reais, já arredondado para dinheiro. */
export const microsEmReais = (micros: number, dolar = DOLAR_PADRAO): number =>
  Math.round((Number(micros) / 1e6) * dolar * 100) / 100;

/**
 * O custo de um dia que foi gravado antes dos microdólares existirem.
 *
 * Os documentos velhos têm `centavosUsd`, e quase todos valem zero pelo
 * motivo do cabeçalho. Converter em vez de ignorar mantém o gráfico
 * contínuo; o que estiver zerado lá continua zerado, e é honesto que
 * continue — aquele dia não foi medido.
 */
export const microsDoDia = (d: { microsUsd?: unknown; centavosUsd?: unknown }): number => {
  const micros = Number(d.microsUsd);
  if (Number.isFinite(micros) && micros > 0) return micros;
  const centavos = Number(d.centavosUsd);
  return Number.isFinite(centavos) ? centavos * 10_000 : 0;
};
